import { useState, useEffect, useCallback, useRef } from 'react';
import {
  isGitHubConnected,
  getSelectedProject,
  fetchProjectBoard,
  type BoardData,
} from '../utils/github';

interface UseProjectBoardResult {
  board: BoardData | null;
  loading: boolean;
  error: string | null;
  needsGitHub: boolean;    // true if user hasn't connected GitHub
  needsProject: boolean;   // true if user hasn't selected a project
  refresh: () => void;
}

const REFRESH_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

export function useProjectBoard(): UseProjectBoardResult {
  const [board, setBoard] = useState<BoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const connected = isGitHubConnected();
  const selectedProject = getSelectedProject();

  const fetchBoard = useCallback(async () => {
    if (!isGitHubConnected()) {
      setLoading(false);
      return;
    }
    const project = getSelectedProject();
    if (!project?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await fetchProjectBoard(project.id);
      setBoard(data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load board';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    void fetchBoard();
  }, [fetchBoard]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    if (!connected || !selectedProject) return;

    intervalRef.current = setInterval(() => {
      void fetchBoard();
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchBoard, connected, selectedProject]);

  return {
    board,
    loading,
    error,
    needsGitHub: !connected,
    needsProject: connected && !selectedProject,
    refresh: fetchBoard,
  };
}
